import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../api/axios";

const STATUS_BADGE = {
  pending: "bg-secondary",
  processing: "bg-info text-dark",
  distributed: "bg-success",
  retry_scheduled: "bg-warning text-dark",
  outcome_unknown: "bg-warning text-dark",
  action_required: "bg-danger",
  conflict: "bg-danger",
  cancelled_before_send: "bg-light text-dark border",
  reversal_pending: "bg-info text-dark",
  reversing: "bg-info text-dark",
  reversal_retry_scheduled: "bg-warning text-dark",
  reversal_action_required: "bg-danger",
  reversed: "bg-dark",
};

const FILTERS = [
  { value: "attention", label: "Needs action" },
  { value: "", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "retry_scheduled", label: "Retrying" },
  { value: "outcome_unknown", label: "Unknown" },
  { value: "distributed", label: "Distributed" },
  { value: "reversed", label: "Reversed" },
];

const label = (status) => (status || "").replace(/_/g, " ");
const formatDate = (value) => (value ? new Date(value).toLocaleString() : "—");
const formatPoints = (value) => (value == null ? "—" : Number(value).toLocaleString());

/**
 * Sale commissions sent to Iconic Unity (Admin / Superadmin). Shows each event's state and last error, and
 * re-queues the same event with Retry. Retry never creates a new reference.
 */
export default function UnityCommissionsPage() {
  const [status, setStatus] = useState("attention");
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page };
      if (status) params.status = status;
      const res = await api.get("/api/admin/unity-commissions", { params });
      setRows(res.data?.data || []);
      setMeta(res.data?.meta || null);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not load Unity commissions.");
    } finally {
      setLoading(false);
    }
  }, [status, page]);

  useEffect(() => {
    load();
  }, [load]);

  const retry = async (row, rebuild = false) => {
    if (busyId) return;
    if (rebuild && !window.confirm("Rebuild the commission from the current sale data and send it? Only do this after fixing the unit, seller or price.")) {
      return;
    }
    setBusyId(row.id);
    try {
      const res = await api.post(`/api/admin/unity-commissions/${row.id}/retry`, rebuild ? { rebuild: true } : {});
      toast.success(res.data?.message || "Queued again.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not retry this commission.");
    } finally {
      setBusyId(null);
      await load();
    }
  };

  return (
    <div className="container-fluid">
      <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
        <h3 className="fw-bold text-primary mb-0">
          Unity Commissions
          {meta?.attention_count > 0 && (
            <span className="badge bg-danger ms-2">{meta.attention_count} need action</span>
          )}
        </h3>
        <div className="btn-group flex-wrap" role="group" aria-label="Filter by status">
          {FILTERS.map((f) => (
            <button
              key={f.value || "all"}
              type="button"
              className={`btn btn-sm ${status === f.value ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => {
                setPage(1);
                setStatus(f.value);
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <p className="text-muted">
        Each confirmed sale sends its full price once to Iconic Unity, which converts it to points. A cancelled sale
        reverses it. Retry sends the same commission again with the same reference.
      </p>

      {meta && !meta.delivery_enabled && (
        <div className="alert alert-secondary">
          Sending is turned off (UNITY_COMMISSION_DELIVERY_ENABLED=false). Sales are recorded as pending and sent once it
          is turned on.
        </div>
      )}
      {meta?.sending_stopped && (
        <div className="alert alert-danger">
          <strong>Sending is stopped.</strong> Unity rejected the API token or URL ({meta.sending_stopped.code}):{" "}
          {meta.sending_stopped.message} Fix the server setting, then press Retry on one commission to resume.
        </div>
      )}

      {loading ? (
        <div>Loading...</div>
      ) : rows.length === 0 ? (
        <div className="alert alert-light border text-center">No commissions here.</div>
      ) : (
        <div className="table-responsive shadow-sm rounded">
          <table className="table table-hover table-bordered align-middle mb-0">
            <thead className="table-primary">
              <tr>
                <th>Reference</th>
                <th>Sale</th>
                <th>Seller</th>
                <th className="text-end">Amount (৳)</th>
                <th>Category</th>
                <th>Status</th>
                <th className="text-end">Points</th>
                <th>Attempts</th>
                <th>Last error</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="text-nowrap">
                    {row.reference_id}
                    {row.reversal_reference && <div className="small text-muted">{row.reversal_reference}</div>}
                  </td>
                  <td>
                    {row.property_name || "—"}
                    {row.unit_number && <div className="small text-muted">Unit {row.unit_number}</div>}
                    <div className="small text-muted">Booking #{row.booking_id}</div>
                  </td>
                  <td className="small">{row.seller_email || "—"}</td>
                  <td className="text-end">{row.amount ? Number(row.amount).toLocaleString() : "—"}</td>
                  <td className="small">{row.product_id ? `${row.service_id} / ${row.product_id}` : "—"}</td>
                  <td>
                    <span className={`badge ${STATUS_BADGE[row.status] || "bg-secondary"}`}>{label(row.status)}</span>
                    {row.next_attempt_at && <div className="small text-muted">next {formatDate(row.next_attempt_at)}</div>}
                    {row.cancel_requested_at && !row.reversed_at && (
                      <div className="small text-muted">sale cancelled {formatDate(row.cancel_requested_at)}</div>
                    )}
                  </td>
                  <td className="text-end">
                    {row.status === "distributed" && formatPoints(row.points_distributed)}
                    {row.status === "reversed" && <span>−{formatPoints(row.points_reversed)}</span>}
                    {row.negative_balance_user_ids?.length > 0 && (
                      <div className="small text-info">
                        {row.negative_balance_user_ids.length} recipient(s) had already withdrawn points
                      </div>
                    )}
                  </td>
                  <td>{row.reversal_attempts > 0 ? `${row.attempts} + ${row.reversal_attempts}` : row.attempts}</td>
                  <td className="small" style={{ maxWidth: 320 }}>
                    {row.last_error_code && <code>{row.last_error_code}</code>}
                    {row.last_http_status && <span className="text-muted"> (HTTP {row.last_http_status})</span>}
                    {row.last_error_message && <div>{row.last_error_message}</div>}
                  </td>
                  <td className="text-nowrap">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary me-1"
                      disabled={!row.can_retry || busyId === row.id}
                      onClick={() => retry(row)}
                    >
                      Retry
                    </button>
                    {row.can_rebuild && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={busyId === row.id}
                        onClick={() => retry(row, true)}
                      >
                        Fix &amp; retry
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {meta && meta.last_page > 1 && (
        <div className="d-flex justify-content-end align-items-center gap-2 mt-3">
          <button type="button" className="btn btn-sm btn-outline-primary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            Previous
          </button>
          <span className="small">
            Page {meta.current_page} of {meta.last_page}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            disabled={page >= meta.last_page}
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
